"use client";

import { useRef, useState, useCallback, type FormEvent } from "react";
import { trackExperimentExposure } from "@/lib/experiment";
import Link from "next/link";
import type { ContactPreview } from "./KundaliFormCard.submit";
import { PhoneField } from "./KundaliFormCard.contact-fields";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { isContactGateSatisfied } from "./KundaliFormCard.validation";
import BirthTimePrecisionNotice from "@/components/BirthTimePrecisionNotice";

export default function KundaliContactPreview({
  preview,
  language,
}: {
  preview: ContactPreview;
  language: KundaliLang;
}) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const hi = language === "hi";
  async function continueReading(event: FormEvent) {
    event.preventDefault();
    if (pending.current) return;
    if (!isContactGateSatisfied(phone)) {
      setError(hi ? "कृपया सही मोबाइल नंबर भरें।" : "Enter a valid mobile number.");
      return;
    }
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await preview.continueWithContact(phone);
    } catch {
      setError(
        hi
          ? "आपकी कुंडली तैयार है। नंबर सेव नहीं हुआ; फिर कोशिश करें।"
          : "Your chart is ready. Your number could not be saved; try again."
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  const result = preview.result;
  const stable = result.time_sensitivity?.stable_planets;
  const planets = result.birth_time_known
    ? result.planets
    : result.planets.filter((p) => Array.isArray(stable) && stable.includes(p.planet_id));
  return (
    <section
      className="card-sacred mx-auto my-6 max-w-2xl space-y-5 p-5 sm:p-8"
      aria-label={hi ? "आपकी कुंडली की पहली झलक" : "Your first chart result"}
    >
      <h2 className="font-serif text-title font-bold">
        {hi ? "आपकी गणना तैयार है" : "Your calculated chart is ready"}
      </h2>
      <p>
        {hi
          ? "नीचे गणना से मिली ग्रह राशियाँ हैं। यह खगोलीय गणना है; इससे किसी भविष्यवाणी की पुष्टि नहीं होती।"
          : "These planetary signs come from your birth details. They are astronomical calculations, not proof of a prediction."}
      </p>
      {!result.birth_time_known && (
        <BirthTimePrecisionNotice subject="chart" timeSensitivity={result.time_sensitivity} />
      )}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {planets.map((planet) => (
          <div key={planet.planet_id} className="rounded-lg border border-border-default p-3">
            <dt className="capitalize text-caption text-text-muted">{planet.planet_id}</dt>
            <dd className="font-semibold">{planet.rashi}</dd>
          </div>
        ))}
      </dl>
      <Link
        href="/research/birth-time-problem-2026/#compare-birth-times"
        className="block text-body-small underline"
      >
        {hi
          ? "जन्म समय में अंतर से क्या बदलता है?"
          : "See what changes when your birth time changes"}
      </Link>
      <form onSubmit={continueReading} className="space-y-4">
        <p>
          {hi
            ? "इस कुंडली को सेव करके आगे पढ़ने के लिए मोबाइल नंबर जोड़ें। मार्केटिंग की सहमति अलग है।"
            : "Add your mobile number to save this chart and continue your reading. Marketing needs separate consent."}
        </p>
        <PhoneField
          value={phone}
          onChange={setPhone}
          onFocus={() => {}}
          t={getKundaliTranslations(language)}
          required
          error={error || undefined}
        />
        <button
          type="submit"
          disabled={busy}
          className="min-h-11 w-full rounded-xl bg-surface-brand-strong px-4 py-3 font-semibold text-text-on-brand"
        >
          {busy
            ? hi
              ? "सेव हो रहा है…"
              : "Saving…"
            : hi
              ? "सेव करके आगे पढ़ें"
              : "Save and continue"}
        </button>
      </form>
    </section>
  );
}

export function useKundaliPreview(
  phone: string,
  progressive: boolean,
  paidEntry: boolean,
  language: KundaliLang,
  label?: string
) {
  const [preview, setPreview] = useState<ContactPreview | null>(null);
  const [enabled, setEnabled] = useState(false);
  const onStarted = useCallback(
    (firstField?: string) => {
      const path = window.location.pathname.replace(/\/$/, "");
      if (
        firstField === "phone" ||
        firstField === "email" ||
        phone ||
        progressive ||
        paidEntry ||
        !["/ai-astrologer", "/free-kundali", "/hi/free-kundali"].includes(path)
      )
        return;
      setEnabled(
        trackExperimentExposure("kundali_preview_pilot_2026_09", undefined, { placement: path }) ===
          "preview"
      );
    },
    [phone, progressive, paidEntry]
  );
  return {
    preview,
    reset: () => setPreview(null),
    renderBefore: (result: unknown) =>
      preview && !result ? <KundaliContactPreview preview={preview} language={language} /> : null,
    enabled,
    onStarted,
    onContactPreview: enabled ? setPreview : undefined,
    submitLabel: enabled
      ? language === "hi"
        ? "पहले मेरी कुंडली देखें"
        : "See my chart first"
      : label,
  };
}
